import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, AlertTriangle, Copy, Check, X, Save, Upload, Download, AlertCircle, CalendarClock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { CostPriceImporter } from '../../components/admin/CostPriceImporter';
import { ProductExportModal } from '../../components/admin/ProductExportModal';
import { QuickPreorderModal } from '../../components/admin/QuickPreorderModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProducts = () => {
  const { isAdmin, isStaff } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [missingCostOnly, setMissingCostOnly] = useState(false);
  // Low-stock filter — activated when arriving from the header notification
  // bell via /admin/produits?filter=low_stock. Shows only products where
  // stock <= min_stock (or ≤ 5 if min_stock is unset), stock > 0.
  const [lowStockOnly, setLowStockOnly] = useState(
    searchParams.get('filter') === 'low_stock'
  );
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [showCostImport, setShowCostImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkValue, setBulkValue] = useState('');
  const [preorderTargets, setPreorderTargets] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      // Error shown via loading=false + null data check in UI
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isStaff) { navigate('/connexion'); return; }
    fetchProducts();
  }, [isStaff, navigate, fetchProducts]);

  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category).filter(Boolean))];
    return cats.sort();
  }, [products]);

  const brands = useMemo(() => {
    const bs = [...new Set(products.map(p => p.brand).filter(Boolean))];
    return bs.sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    const filtered = products.filter(p => {
      const matchSearch = !search ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.brand.toLowerCase().includes(search.toLowerCase()) ||
        (p.barcode && p.barcode.includes(search));
      const matchCategory = categoryFilter === 'all' || p.category === categoryFilter;
      const matchBrand = brandFilter === 'all' || p.brand === brandFilter;
      const matchMissingCost = !missingCostOnly || !p.cost_price || p.cost_price <= 0;
      // Low-stock: mirrors backend logic in GET /api/admin/notifications
      let matchLowStock = true;
      if (lowStockOnly) {
        const stock = Number(p.stock) || 0;
        const raw = p.min_stock;
        const threshold = raw == null || raw === '' || isNaN(Number(raw)) ? 5 : Number(raw);
        matchLowStock = threshold > 0 && stock > 0 && stock <= threshold && p.is_available !== false;
      }
      return matchSearch && matchCategory && matchBrand && matchMissingCost && matchLowStock;
    });

    const cmpStr = (a, b) => (a || '').toLocaleLowerCase().localeCompare((b || '').toLocaleLowerCase());
    const cmpNum = (a, b) => (Number(a) || 0) - (Number(b) || 0);

    const sorted = [...filtered];
    switch (sortBy) {
      case 'name_asc':       sorted.sort((a, b) => cmpStr(a.name, b.name)); break;
      case 'name_desc':      sorted.sort((a, b) => cmpStr(b.name, a.name)); break;
      case 'brand_asc':      sorted.sort((a, b) => cmpStr(a.brand, b.brand) || cmpStr(a.name, b.name)); break;
      case 'brand_desc':     sorted.sort((a, b) => cmpStr(b.brand, a.brand) || cmpStr(a.name, b.name)); break;
      case 'category_asc':   sorted.sort((a, b) => cmpStr(a.category, b.category) || cmpStr(a.brand, b.brand)); break;
      case 'category_desc':  sorted.sort((a, b) => cmpStr(b.category, a.category) || cmpStr(a.brand, b.brand)); break;
      case 'stock_asc':      sorted.sort((a, b) => cmpNum(a.stock, b.stock)); break;
      case 'stock_desc':     sorted.sort((a, b) => cmpNum(b.stock, a.stock)); break;
      case 'price_asc':      sorted.sort((a, b) => cmpNum(a.price_detail, b.price_detail)); break;
      case 'price_desc':     sorted.sort((a, b) => cmpNum(b.price_detail, a.price_detail)); break;
      case 'price_gros_asc':  sorted.sort((a, b) => cmpNum(a.price_gros, b.price_gros)); break;
      case 'price_gros_desc': sorted.sort((a, b) => cmpNum(b.price_gros, a.price_gros)); break;
      case 'cost_asc':       sorted.sort((a, b) => cmpNum(a.cost_price, b.cost_price)); break;
      case 'cost_desc':      sorted.sort((a, b) => cmpNum(b.cost_price, a.cost_price)); break;
      default: break;
    }
    return sorted;
  }, [products, search, categoryFilter, brandFilter, missingCostOnly, lowStockOnly, sortBy]);

  const missingCostCount = useMemo(
    () => products.filter(p => !p.cost_price || p.cost_price <= 0).length,
    [products],
  );

  const handleDelete = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    setDeleting(productId);
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Produit supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleDuplicate = async (productId) => {
    try {
      const response = await axios.post(`${API_URL}/api/products/${productId}/duplicate`, {});
      setProducts([response.data, ...products]);
      toast.success('Produit dupliqué');
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === filteredProducts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleBulkApply = async () => {
    if (!bulkAction || !bulkValue || selected.size === 0) return;

    const value = parseFloat(bulkValue);
    if (isNaN(value)) { toast.error('Valeur invalide'); return; }

    let updated = 0;
    for (const productId of selected) {
      try {
        const product = products.find(p => p.id === productId);
        if (!product) continue;

        const payload = {};
        if (bulkAction === 'price_detail') payload.price_detail = value;
        else if (bulkAction === 'price_semi_gros') payload.price_semi_gros = value;
        else if (bulkAction === 'price_gros') payload.price_gros = value;
        else if (bulkAction === 'cost_price') payload.cost_price = value;
        else if (bulkAction === 'stock') payload.stock = Math.round(value);
        else if (bulkAction === 'min_stock') payload.min_stock = Math.round(value);
        else if (bulkAction === 'price_increase_pct') {
          payload.price_detail = Math.round(product.price_detail * (1 + value / 100) * 100) / 100;
          payload.price_semi_gros = Math.round(product.price_semi_gros * (1 + value / 100) * 100) / 100;
          payload.price_gros = Math.round(product.price_gros * (1 + value / 100) * 100) / 100;
        }

        await axios.put(`${API_URL}/api/products/${productId}`, payload);
        updated++;
      } catch (err) {
        // individual error, continue batch
      }
    }

    toast.success(`${updated} produit(s) mis à jour`);
    setBulkAction(null);
    setBulkValue('');
    setSelected(new Set());
    setBulkMode(false);
    fetchProducts();
  };

  const getStockTooltip = (product) => {
    const min = product.min_stock ?? 5;
    if (product.stock === 0) return 'Rupture de stock complète';
    if (product.stock <= min) return `Stock bas : ${product.stock} ≤ seuil ${min}`;
    return `${product.stock} en stock (seuil : ${min})`;
  };

  // ---- Inline edit (stock + min_stock) ----
  const [editing, setEditing] = useState({ id: null, field: null, value: '' });

  const startEdit = (product, field) => {
    const value = field === 'min_stock' ? (product.min_stock ?? 5) : product.stock;
    setEditing({ id: product.id, field, value: String(value) });
  };

  const cancelEdit = () => setEditing({ id: null, field: null, value: '' });

  const saveEdit = async () => {
    const { id, field, value } = editing;
    const n = parseInt(value, 10);
    if (!id || !field || isNaN(n) || n < 0) {
      toast.error('Valeur invalide');
      return;
    }
    try {
      if (field === 'stock') {
        await axios.put(`${API_URL}/api/products/${id}/stock?stock=${n}`);
      } else {
        await axios.put(`${API_URL}/api/products/${id}`, { [field]: n });
      }
      setProducts((prev) => prev.map((p) => p.id === id ? { ...p, [field]: n } : p));
      toast.success('Mis à jour');
      cancelEdit();
    } catch (e) {
      toast.error('Échec de la sauvegarde');
    }
  };

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-block">
              ← Retour au dashboard
            </Link>
            <h1 className="text-4xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Gestion des Produits
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {isStaff && (
              <button
                type="button"
                onClick={() => { setBulkMode(!bulkMode); setSelected(new Set()); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                  bulkMode ? 'bg-white/10 text-white border border-white/20' : 'border border-white/10 text-zinc-400 hover:text-white'
                }`}
                data-testid="bulk-mode-btn"
              >
                <Check className="w-4 h-4" />
                {bulkMode ? 'Annuler sélection' : 'Modifier en lot'}
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowCostImport(true)}
                className="flex items-center gap-2 border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 px-4 py-2 text-sm transition-colors"
                data-testid="import-cost-btn"
              >
                <Upload className="w-4 h-4" />
                Importer prix d'achat
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowExport(true)}
              className="flex items-center gap-2 border border-white/10 text-zinc-300 hover:text-white hover:border-white/30 px-4 py-2 text-sm transition-colors"
              data-testid="export-products-btn"
            >
              <Download className="w-4 h-4" />
              Exporter
            </button>
            <Link to="/admin/produits/nouveau" className="btn-primary flex items-center gap-2" data-testid="add-product-btn">
              <Plus className="w-4 h-4" /> Nouveau Produit
            </Link>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              placeholder="Rechercher par nom, marque ou code EAN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full input-field pl-12"
              data-testid="search-products"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-[color:var(--color-surface)] border border-white/10 text-zinc-300 px-4 py-3 outline-none focus:border-[color:var(--color-primary)] min-w-[160px]"
            data-testid="filter-category"
          >
            <option value="all">Toutes catégories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="bg-[color:var(--color-surface)] border border-white/10 text-zinc-300 px-4 py-3 outline-none focus:border-[color:var(--color-primary)] min-w-[160px]"
            data-testid="filter-brand"
          >
            <option value="all">Toutes marques</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-[color:var(--color-surface)] border border-white/10 text-zinc-300 px-4 py-3 outline-none focus:border-[color:var(--color-primary)] min-w-[200px]"
            data-testid="sort-products"
            title="Trier les produits"
          >
            <optgroup label="Nom">
              <option value="name_asc">Nom A → Z</option>
              <option value="name_desc">Nom Z → A</option>
            </optgroup>
            <optgroup label="Marque">
              <option value="brand_asc">Marque A → Z</option>
              <option value="brand_desc">Marque Z → A</option>
            </optgroup>
            <optgroup label="Catégorie">
              <option value="category_asc">Catégorie A → Z</option>
              <option value="category_desc">Catégorie Z → A</option>
            </optgroup>
            <optgroup label="Stock">
              <option value="stock_asc">Stock croissant ↑</option>
              <option value="stock_desc">Stock décroissant ↓</option>
            </optgroup>
            <optgroup label="Prix">
              <option value="price_asc">Prix détail ↑</option>
              <option value="price_desc">Prix détail ↓</option>
              <option value="price_gros_asc">Prix gros ↑</option>
              <option value="price_gros_desc">Prix gros ↓</option>
              {isAdmin && <option value="cost_asc">Prix d'achat ↑</option>}
              {isAdmin && <option value="cost_desc">Prix d'achat ↓</option>}
            </optgroup>
          </select>
        </div>

        {/* Low-stock filter banner (activated via ?filter=low_stock from notifications) */}
        {lowStockOnly && (
          <div
            className="flex items-center justify-between gap-3 border border-amber-500/30 bg-amber-500/10 px-4 py-3 mb-4"
            data-testid="low-stock-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <p className="text-amber-200 text-sm">
                Filtre <span className="font-medium">Stock bas</span> actif — {filteredProducts.length} produit(s) affichés
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setLowStockOnly(false);
                const next = new URLSearchParams(searchParams);
                next.delete('filter');
                setSearchParams(next, { replace: true });
              }}
              data-testid="clear-low-stock-filter"
              className="flex-shrink-0 text-xs uppercase tracking-wider px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors"
            >
              Voir tous les produits
            </button>
          </div>
        )}

        {/* Cost-price missing banner */}
        {isAdmin && missingCostCount > 0 && (
          <div
            className="flex items-center justify-between gap-3 border border-amber-500/30 bg-amber-500/10 px-4 py-3 mb-4"
            data-testid="missing-cost-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <p className="text-amber-200 text-sm">
                <span className="font-medium">{missingCostCount}</span> produit(s) n'ont pas de prix d'achat — la marge et la valeur du stock ne peuvent pas être calculées pour eux.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setMissingCostOnly(!missingCostOnly)}
              data-testid="toggle-missing-cost-filter"
              className={`flex-shrink-0 text-xs uppercase tracking-wider px-3 py-1.5 transition-colors ${
                missingCostOnly
                  ? 'bg-amber-500 text-black'
                  : 'border border-amber-500/40 text-amber-300 hover:bg-amber-500/10'
              }`}
            >
              {missingCostOnly ? 'Voir tous les produits' : 'Afficher uniquement ceux-ci'}
            </button>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {bulkMode && selected.size > 0 && isStaff && (
          <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="bulk-actions-bar">
            <span className="text-[color:var(--color-primary)] text-sm">{selected.size} sélectionné(s)</span>
            <button
              type="button"
              onClick={() => setPreorderTargets(filteredProducts.filter(p => selected.has(p.id)))}
              className="flex items-center gap-1 border border-[color:rgb(var(--primary-rgb)/0.40)] text-[color:var(--color-primary)] text-sm px-3 py-2 hover:bg-[color:var(--color-primary)] hover:text-black transition-colors"
              data-testid="bulk-preorder-btn"
            >
              <CalendarClock className="w-3 h-3" /> Mettre en précommande
            </button>
            <select
              value={bulkAction || ''}
              onChange={(e) => setBulkAction(e.target.value || null)}
              className="bg-transparent border border-white/10 text-zinc-300 text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)]"
              data-testid="bulk-action-select"
            >
              <option value="">Choisir une action</option>
              <option value="price_detail">Prix détail (€)</option>
              <option value="price_semi_gros">Prix semi-gros (€)</option>
              <option value="price_gros">Prix gros (€)</option>
              {isAdmin && <option value="cost_price">Prix d'achat (€)</option>}
              <option value="price_increase_pct">Augmenter tous prix (%)</option>
              <option value="stock">Stock (unités)</option>
              <option value="min_stock">Seuil d'alerte stock (unités)</option>
            </select>
            {bulkAction && (
              <>
                <input
                  type="number"
                  step="0.01"
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  placeholder={bulkAction === 'price_increase_pct' ? 'Ex: 10 pour +10%' : 'Valeur'}
                  className="bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-[color:var(--color-primary)] w-36"
                  data-testid="bulk-value-input"
                />
                <button
                  type="button"
                  onClick={handleBulkApply}
                  className="flex items-center gap-1 bg-[color:var(--color-primary)] text-black text-sm px-4 py-2 hover:bg-[color:var(--color-primary-hover)] transition-colors"
                  data-testid="bulk-apply-btn"
                >
                  <Save className="w-3 h-3" /> Appliquer
                </button>
              </>
            )}
          </div>
        )}

        {/* Count */}
        <p className="text-zinc-500 text-sm mb-3">{filteredProducts.length} produit(s)</p>

        {/* Products Table */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-[color:var(--color-surface)] border border-white/10 h-20 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="bg-[color:var(--color-surface)] border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    {bulkMode && (
                      <th className="p-4 w-10">
                        <input
                          type="checkbox"
                          checked={selected.size === filteredProducts.length && filteredProducts.length > 0}
                          onChange={toggleSelectAll}
                          className="accent-[color:var(--color-primary)] w-4 h-4"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                    )}
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Produit</th>
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Catégorie</th>
                    {isStaff && <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Prix</th>}
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Stock</th>
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider" title="Seuil d'alerte stock bas">Seuil</th>
                    <th className="text-right p-4 text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="products-table">
                  {filteredProducts.map(product => (
                    <tr
                      key={product.id}
                      className={`border-b border-white/5 hover:bg-white/5 transition-colors ${selected.has(product.id) ? 'bg-[color:rgb(var(--primary-rgb)/0.05)]' : ''}`}
                      data-testid={`product-row-${product.id}`}
                    >
                      {bulkMode && (
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selected.has(product.id)}
                            onChange={() => toggleSelect(product.id)}
                            className="accent-[color:var(--color-primary)] w-4 h-4"
                            data-testid={`select-${product.id}`}
                          />
                        </td>
                      )}
                      <td className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-[color:var(--color-surface)] overflow-hidden flex-shrink-0">
                            <img
                              src={product.image_url}
                              alt={product.name}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=100'; }}
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white">{product.name}</p>
                              {isAdmin && (!product.cost_price || product.cost_price <= 0) && (
                                <span
                                  className="inline-flex items-center gap-1 text-amber-400 text-[10px] uppercase tracking-wider border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 cursor-help"
                                  title="Prix d'achat non renseigné — la marge et la valeur du stock ne peuvent pas être calculées pour ce produit"
                                  data-testid={`no-cost-warning-${product.id}`}
                                >
                                  <AlertCircle className="w-3 h-3" />
                                  Prix d'achat manquant
                                </span>
                              )}
                            </div>
                            <p className="text-zinc-500 text-sm">{product.brand}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-zinc-400">{product.category}</td>
                      {isStaff && (
                        <td className="p-4">
                          <div className="text-sm">
                            <p className="text-[color:var(--color-primary)]">{product.price_detail}€ <span className="text-zinc-600">détail</span></p>
                            <p className="text-zinc-500">{product.price_semi_gros}€ / {product.price_gros}€</p>
                          </div>
                        </td>
                      )}
                      <td className="p-4">
                        {editing.id === product.id && editing.field === 'stock' ? (
                          <input
                            type="number"
                            min="0"
                            value={editing.value}
                            autoFocus
                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                            onBlur={saveEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEdit();
                              else if (e.key === 'Escape') cancelEdit();
                            }}
                            data-testid={`stock-input-${product.id}`}
                            className="w-20 bg-transparent border border-[color:var(--color-primary)] text-white px-2 py-1 text-sm outline-none"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEdit(product, 'stock')}
                            title={getStockTooltip(product) + ' — Cliquez pour modifier'}
                            data-testid={`stock-cell-${product.id}`}
                            className="flex items-center gap-1 text-sm hover:underline decoration-dotted decoration-zinc-500 underline-offset-4 cursor-pointer"
                          >
                            {product.stock === 0 ? (
                              <span className="flex items-center gap-1 text-red-500">
                                <AlertTriangle className="w-4 h-4" />
                                Rupture
                              </span>
                            ) : product.stock <= (product.min_stock ?? 5) ? (
                              <span className="flex items-center gap-1 text-yellow-500">
                                <AlertTriangle className="w-4 h-4" />
                                {product.stock}
                              </span>
                            ) : (
                              <span className="text-green-500">{product.stock}</span>
                            )}
                          </button>
                        )}
                        {product.preorder_enabled && (
                          <Link to="/admin/precommandes"
                            className="mt-1 inline-block text-[10px] uppercase tracking-wider text-[color:var(--color-primary)] border border-[color:rgb(var(--primary-rgb)/0.40)] px-1.5 py-0.5"
                            data-testid={`preorder-tag-${product.id}`}>
                            Arrivage {product.preorder_expected_date ? new Date(product.preorder_expected_date).toLocaleDateString('fr-FR') : ''}
                            {product.is_published === false ? ' · masqué' : ''}
                          </Link>
                        )}
                      </td>
                      <td className="p-4">
                        {editing.id === product.id && editing.field === 'min_stock' ? (
                          <input
                            type="number"
                            min="0"
                            value={editing.value}
                            autoFocus
                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                            onBlur={saveEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEdit();
                              else if (e.key === 'Escape') cancelEdit();
                            }}
                            data-testid={`min-stock-input-${product.id}`}
                            className="w-20 bg-transparent border border-[color:var(--color-primary)] text-white px-2 py-1 text-sm outline-none"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEdit(product, 'min_stock')}
                            title="Seuil d'alerte — cliquez pour modifier"
                            data-testid={`min-stock-cell-${product.id}`}
                            className="text-zinc-400 text-sm hover:text-[color:var(--color-primary)] hover:underline decoration-dotted decoration-zinc-600 underline-offset-4 cursor-pointer"
                          >
                            {product.min_stock ?? 5}
                          </button>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setPreorderTargets([product])}
                            className="p-2 hover:bg-white/10 transition-colors"
                            title={product.preorder_enabled ? 'Modifier la précommande' : 'Mettre en précommande (arrivage fournisseur)'}
                            data-testid={`preorder-product-btn-${product.id}`}
                          >
                            <CalendarClock className={`w-4 h-4 ${product.preorder_enabled ? 'text-[color:var(--color-primary)]' : (product.stock || 0) <= (product.min_stock ?? 5) ? 'text-orange-400 hover:text-[color:var(--color-primary)]' : 'text-zinc-400 hover:text-[color:var(--color-primary)]'}`} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDuplicate(product.id)}
                            className="p-2 hover:bg-white/10 transition-colors"
                            title="Dupliquer"
                            data-testid={`duplicate-product-${product.id}`}
                          >
                            <Copy className="w-4 h-4 text-zinc-400 hover:text-[color:var(--color-primary)]" />
                          </button>
                          <Link
                            to={`/admin/produits/${product.id}`}
                            className="p-2 hover:bg-white/10 transition-colors"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="w-4 h-4 text-zinc-400 hover:text-white" />
                          </Link>
                          {isStaff && (
                            <button
                              type="button"
                              onClick={() => handleDelete(product.id)}
                              disabled={deleting === product.id}
                              className="p-2 hover:bg-white/10 transition-colors disabled:opacity-50"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-500" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-zinc-500">Aucun produit trouvé</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSV Import for cost prices */}
      <CostPriceImporter
        open={showCostImport}
        onClose={() => setShowCostImport(false)}
        onImported={() => fetchProducts()}
      />
      <ProductExportModal
        open={showExport}
        onClose={() => setShowExport(false)}
        filteredIds={filteredProducts.map(p => p.id)}
        totalCount={products.length}
      />
      {preorderTargets && (
        <QuickPreorderModal
          products={preorderTargets}
          onClose={() => setPreorderTargets(null)}
          onDone={() => { setPreorderTargets(null); setSelected(new Set()); setBulkMode(false); fetchProducts(); }}
        />
      )}
    </div>
  );
};

export default AdminProducts;
